import { Skeleton, useToast } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  errorToastConfig,
  getDefaultDashboardDisplayEndDate,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetSuccessFinancialCovenantsByCreditFacilityIdQuery } from '~/packages/covenants/requests/financialCovenants.gql.generated';
import { CovenantChartTemplate } from '~/packages/portfolio/ui/covenants/CovenantChartTemplate';
import { createCovenantsChartConfig } from '~/packages/portfolio/ui/covenants/CovenantsContainer';
import {
  covenantsInitialStartDate,
  defaultFinancialCovenantValueType,
  financialCovenantValueTypeToPortfolioChartConfigType,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';

type DigitalMonthlyReportSingleFinancialCovenantDetailProps = {
  creditFacilityId: string;
  creditAgreementFinancialCovenants: CreditAgreement.DomainEntity.FinancialCovenantProperty[];
  covenantKey: string;
};
export const DigitalMonthlyReportSingleFinancialCovenantDetail = ({
  creditFacilityId,
  creditAgreementFinancialCovenants,
  covenantKey,
}: DigitalMonthlyReportSingleFinancialCovenantDetailProps) => {
  const toast = useToast();

  const {
    loading: isFinancialCovenantsLoading,
    data: allFinancialCovenantsData,
    refetch: refetchSuccessFinancialCovenants,
  } = useGetSuccessFinancialCovenantsByCreditFacilityIdQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      mode: 'RESULT',
      startDate: covenantsInitialStartDate,
      endDate: getDefaultDashboardDisplayEndDate(),
    },
  });

  // it's possible for the covenantKey of a financial covenant calculation to no longer exist in the current CA, if it's been deleted
  // we will still show the calculations and graphs as historical data, but will not be able to parse out the thresholdType (min vs max) later downstream, which is fine
  const creditAgreementFinancialCovenant:
    | CreditAgreement.DomainEntity.FinancialCovenantProperty
    | undefined = useMemo(() => {
    return creditAgreementFinancialCovenants.find(
      (financialCovenant) => financialCovenant.covenantKey === covenantKey,
    );
  }, [covenantKey, creditAgreementFinancialCovenants]);

  const finCovValueType = creditAgreementFinancialCovenant?.limit.valueType;
  const configType = finCovValueType
    ? financialCovenantValueTypeToPortfolioChartConfigType[finCovValueType]
    : 'float';

  if (isFinancialCovenantsLoading || !allFinancialCovenantsData) {
    return (
      <Skeleton
        height="600px"
        data-testid="financial-covenant-results-loading"
      />
    );
  }

  return (
    <CovenantChartTemplate
      creditFacilityId={creditFacilityId}
      chartTitle={commonUtils.toTitleCase(covenantKey)}
      covenantKey={covenantKey}
      chartConfig={createCovenantsChartConfig(configType)}
      key={covenantKey}
      creditAgreementCovenant={creditAgreementFinancialCovenant}
      covenantData={allFinancialCovenantsData.successFinCovs}
      refetchCovenants={refetchSuccessFinancialCovenants}
      covenantType="financial"
      valueType={finCovValueType ?? defaultFinancialCovenantValueType}
    />
  );
};
